import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { submitContact } from "@/lib/api";
import SEO from "@/components/SEO";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Message received. We'll be in touch shortly.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Could not send. Please call us at 956.217.2604.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact — Hurtler Environmental in Edinburg, TX"
        description="Talk to an independent environmental consultant. Hurtler Environmental, 204 E Cano Street, Edinburg TX 78539. 956.217.2604. Serving Texas."
        path="/contact"
      />
      <section className="bg-[#F8FAFC] py-24">
        <div className="container-x">
          <div className="overline mb-5">Contact</div>
          <h1 className="font-display text-5xl sm:text-6xl font-extrabold tracking-tight text-[#0F172A] leading-[1.05]">
            Let's talk about your environmental concern.
          </h1>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-x grid lg:grid-cols-2 gap-16">
          <div className="space-y-8">
            <div>
              <div className="overline mb-3">Headquarters</div>
              <div className="text-slate-700">
                <div className="flex items-start gap-3 mb-3">
                  <MapPin className="w-5 h-5 text-[#7C3AED] mt-0.5" />
                  <div>204 E Cano Street<br />Edinburg, TX 78539</div>
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <Phone className="w-5 h-5 text-[#7C3AED]" />
                  <a href="tel:9562172604" className="hover:text-[#7C3AED]">956.217.2604</a>
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <Mail className="w-5 h-5 text-[#7C3AED]" />
                  <a href="mailto:maria@hurtler.com" className="hover:text-[#7C3AED]">maria@hurtler.com</a>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-[#7C3AED]" />
                  <span>Mon–Fri • 8:00 – 5:00 CT</span>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-slate-200">
              <iframe
                title="Hurtler Environmental HQ map"
                src="https://www.google.com/maps?q=204+E+Cano+St,+Edinburg,+TX+78539&output=embed"
                width="100%"
                height="320"
                style={{ border: 0, filter: "grayscale(40%)" }}
                loading="lazy"
              />
            </div>
          </div>

          <form onSubmit={onSubmit} data-testid="contact-form" className="card-soft p-8 space-y-5">
            <div>
              <label className="text-sm font-semibold text-[#0F172A]">Name</label>
              <input
                data-testid="contact-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:border-[#7C3AED]"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-[#0F172A]">Email</label>
              <input
                data-testid="contact-email-input"
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:border-[#7C3AED]"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-[#0F172A]">Subject</label>
              <input
                data-testid="contact-subject-input"
                required
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="mt-2 w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:border-[#7C3AED]"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-[#0F172A]">Message</label>
              <textarea
                data-testid="contact-message-input"
                required
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="mt-2 w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:border-[#7C3AED]"
              />
            </div>
            <button
              data-testid="contact-submit-button"
              disabled={loading}
              type="submit"
              className="btn-primary w-full justify-center"
            >
              {loading ? "Sending…" : "Send message"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
